import {
  ACCEPTED_IMAGE_FILE_TYPES,
  MAX_TASK_IMAGES,
  useMarkdownImageUpload,
} from '@github-ui/agent-sessions/hooks/use-markdown-image-upload'
import {useClickFileSelect} from '@github-ui/use-unified-file-select'
import {ImageIcon} from '@primer/octicons-react'
import {IconButton, Tooltip} from '@primer/react'
import type {RefObject} from 'react'

import styles from './ImageAttachButton.module.css'

export type ImageAttachButtonProps = {
  /** Ref to the textarea where uploaded image markdown will be inserted. */
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  /** Repository ID used for asset uploads. When undefined, the button is disabled. */
  repositoryId?: string
  /** Whether the button should be disabled (e.g. no repo selected). */
  disabled?: boolean
  /** Whether the image limit has been reached. When true, the button is disabled with a tooltip. */
  isAtImageLimit?: boolean
}

/**
 * Self-contained image attach button that opens a native file picker on click
 * and uploads selected images, inserting markdown into the referenced textarea.
 */
export function ImageAttachButton({textAreaRef, repositoryId, disabled, isAtImageLimit}: ImageAttachButtonProps) {
  const {processFiles, isUploading} = useMarkdownImageUpload({
    textAreaRef,
    repositoryId,
  })

  const clickTargetProps = useClickFileSelect({
    onSelect: (accepted: File[]) => processFiles(accepted),
    multi: true,
    acceptedFileTypes: ACCEPTED_IMAGE_FILE_TYPES,
  })

  const isInactive = disabled || isUploading || isAtImageLimit

  const button = (
    <IconButton
      icon={ImageIcon}
      aria-label={isAtImageLimit ? `Image limit reached (${MAX_TASK_IMAGES})` : 'Attach an image'}
      variant="invisible"
      className={styles.attachButton}
      inactive={isInactive}
      aria-disabled={isInactive}
      {...(!isInactive ? clickTargetProps : {})}
    />
  )

  if (isAtImageLimit) {
    return (
      <Tooltip text={`You can attach up to ${MAX_TASK_IMAGES} images per task`} direction="n">
        {button}
      </Tooltip>
    )
  }

  return button
}
